import * as React from 'react';
import { useSession } from '@/hooks/use-session';
import { parsePinnedProjectIds } from '@/lib/pinned-projects';

const storageKeyFor = (organizationId: string): string =>
  `gainsignal:pinned-projects:${organizationId}`;

type PinSnapshot = {
  storageKey: string | null;
  ids: string[];
};

export const usePinnedProjects = () => {
  const { user } = useSession();
  const storageKey = user?.currentOrganization.id
    ? storageKeyFor(user.currentOrganization.id)
    : null;
  const [snapshot, setSnapshot] = React.useState<PinSnapshot>({
    storageKey: null,
    ids: [],
  });

  React.useEffect(() => {
    if (!storageKey) {
      setSnapshot({ storageKey: null, ids: [] });
      return;
    }
    setSnapshot({
      storageKey,
      ids: parsePinnedProjectIds(localStorage.getItem(storageKey)),
    });

    const syncFromAnotherTab = (event: StorageEvent) => {
      if (event.key === storageKey) {
        setSnapshot({
          storageKey,
          ids: parsePinnedProjectIds(event.newValue),
        });
      }
    };
    window.addEventListener('storage', syncFromAnotherTab);
    return () => window.removeEventListener('storage', syncFromAnotherTab);
  }, [storageKey]);

  const pinned = snapshot.storageKey === storageKey ? snapshot.ids : [];
  const togglePinned = React.useCallback(
    (projectId: string) => {
      if (!storageKey) {
        return;
      }
      setSnapshot((current) => {
        const ids =
          current.storageKey === storageKey
            ? current.ids
            : parsePinnedProjectIds(localStorage.getItem(storageKey));
        const next = ids.includes(projectId)
          ? ids.filter((id) => id !== projectId)
          : [...ids, projectId];
        localStorage.setItem(storageKey, JSON.stringify(next));
        return { storageKey, ids: next };
      });
    },
    [storageKey],
  );

  return { pinned, togglePinned };
};
